import { useState, useMemo } from 'react';
import { useSelector, shallowEqual } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Stack,
  Button,
  Typography,
  useTheme,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  CircularProgress,
  Chip,
  Avatar,
  Tooltip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import StorageIcon from '@mui/icons-material/Storage';
import HelpIcon from '@mui/icons-material/Help';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import TableChartIcon from '@mui/icons-material/TableChart';
import PeopleIcon from '@mui/icons-material/People';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import LinkIcon from '@mui/icons-material/Link';

import dayjs from 'dayjs';
import { useTranslation } from '../common/components/LocalizationProvider';
import PageLayout from '../common/components/PageLayout';
import SettingsMenu from './components/SettingsMenu';
import CollectionActions from './components/CollectionActions';
import { useManager, useDeviceReadonly } from '../common/util/permissions';
import fetchOrThrow from '../common/util/fetchOrThrow';
import { useEffectAsync } from '../reactHelper';
import {
  formatStatus,
  formatTime,
  formatBoolean,
  formatDate,
  getStatusText,
  formatAlarm,
} from '../common/util/formatter';
import { mapIconKey, mapIcons } from '../map/core/preloadImages';
import useCrudExports from '../common/hooks/useCrudExports';
import DeviceUsersValue from './components/DeviceUsersValue';

const DevicesPage = () => {
  const navigate = useNavigate();
  const t = useTranslation();
  const theme = useTheme();
  const manager = useManager();
  const deviceReadonly = useDeviceReadonly();

  const groups = useSelector((state) => state.groups.items);
  const positionsById = useSelector((state) => state.session.positions || {});
  const users = useSelector(
    (state) => {
      const usersObj = state.users?.items;
      return usersObj ? Object.values(usersObj) : [];
    },
    shallowEqual
  );

  const [timestamp, setTimestamp] = useState(Date.now());
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [searchText, setSearchText] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // 🔹 Carregar dispositivos
  useEffectAsync(async () => {
    setLoading(true);
    try {
      const response = await fetchOrThrow('/api/devices');
      const data = await response.json();
      setItems(data);
    } finally {
      setLoading(false);
    }
  }, [timestamp]);

  // 🔹 Estatísticas
  const stats = useMemo(() => {
    const online = items.filter((i) => i.status === 'online').length;
    const offline = items.filter((i) => i.status === 'offline').length;
    const unknown = items.filter((i) => i.status === 'unknown').length;
    return { total: items.length, online, offline, unknown };
  }, [items]);

  // 🔹 Filtragem + Busca
  const filteredItems = useMemo(() => {
    let result = items;
    if (statusFilter) result = result.filter((i) => i.status === statusFilter);
    if (searchText.trim()) {
      const lower = searchText.toLowerCase();
      result = result.filter(
        (i) =>
          i.name?.toLowerCase().includes(lower) ||
          i.uniqueId?.toLowerCase().includes(lower) ||
          i.id?.toString().includes(lower)
      );
    }
    return result;
  }, [items, statusFilter, searchText]);

  const paginated = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredItems.slice(start, start + rowsPerPage);
  }, [filteredItems, page, rowsPerPage]);

  // 🔹 Exportações padronizadas
  const exportColumns = [
    { field: 'id', headerNameKey: 'sharedId' },
    { field: 'name', headerNameKey: 'sharedName' },
    { field: 'uniqueId', headerNameKey: 'deviceIdentifier' },
    {
      field: 'groupId',
      headerNameKey: 'groupParent',
      valueGetter: ({ row }) => (row.groupId ? groups[row.groupId]?.name || '' : ''),
    },
    { field: 'model', headerNameKey: 'deviceModel' },
    { field: 'phone', headerNameKey: 'sharedPhone' },
    { field: 'contact', headerNameKey: 'deviceContact' },
    {
      field: 'expirationTime',
      headerNameKey: 'userExpirationTime',
      valueGetter: ({ row }) => formatTime(row.expirationTime, 'date'),
    },
    {
      field: 'status',
      headerNameKey: 'deviceStatus',
      valueGetter: ({ row }) => formatStatus(row.status, t),
    },
    {
      field: 'lastUpdate',
      headerNameKey: 'deviceLastUpdate',
      valueGetter: ({ row }) => formatTime(row.lastUpdate, 'minutes'),
    },
    {
      field: 'address',
      headerNameKey: 'positionAddress',
      valueGetter: ({ row }) => positionsById[row.id]?.address || '',
    },
  ];

  const { handleExportCsv, handleExportXlsx, handleExportPdf } = useCrudExports({
    titleKey: 'reportDevicesTitle',
    headerKey: 'reportDevicesHeader',
    rows: filteredItems,
    columns: exportColumns,
    theme,
    t,
    options: {
      includeWatermark: true,
    },
  });

  // 🔹 Status
  const statusFilters = [
    { value: '', label: t('sharedAll'), count: stats.total, color: theme.palette.primary.main, Icon: StorageIcon },
    { value: 'online', label: t('deviceStatusOnline'), count: stats.online, color: theme.palette.success.main, Icon: CheckCircleIcon },
    { value: 'offline', label: t('deviceStatusOffline'), count: stats.offline, color: theme.palette.error.main, Icon: ErrorIcon },
    { value: 'unknown', label: t('deviceStatusUnknown'), count: stats.unknown, color: theme.palette.warning.main, Icon: HelpIcon },
  ];

  const getDeviceIcon = (item) =>
    item.photo && item.photo.length < 30
      ? `/photos/devices/${item.photo}`
      : mapIcons[mapIconKey(item.category)] || '/placeholder.svg';

  return (
    <PageLayout menu={<SettingsMenu />} breadcrumbs={['settingsTitle', 'sharedDevices']}>
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
        <Box sx={{ flex: 1, p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Estatísticas */}
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 1 }}>
            {statusFilters.map((f) => (
              <Paper
                key={f.value}
                onClick={() => setStatusFilter(f.value)}
                sx={{
                  flex: '1 1 200px',
                  p: 1.5,
                  cursor: 'pointer',
                  border: 2,
                  borderColor: statusFilter === f.value ? f.color : 'divider',
                  bgcolor: statusFilter === f.value ? f.color + '10' : 'background.paper',
                  '&:hover': { boxShadow: 3, transform: 'translateY(-3px)' },
                }}
              >
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Box
                    sx={{
                      p: 1,
                      borderRadius: 2,
                      bgcolor: f.color + '20',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <f.Icon sx={{ color: f.color, fontSize: 24 }} />
                  </Box>
                  <Box>
                    <Typography variant="h5" fontWeight="bold">
                      {f.count}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {f.label}
                    </Typography>
                  </Box>
                </Stack>
              </Paper>
            ))}
          </Box>

          {/* Barra de Ações */}
          <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">

            {/* Nome do CRUD em negrito */}
            <Typography
              variant="h6"
              fontWeight="bold"
              sx={{ minWidth: 180 }} // ajusta o espaço para manter o layout organizado
            >
              {t('sharedSearchDevices')} {/* ou o texto fixo: "Comandos Salvos" */}
            </Typography>

            <TextField
              size="small"
              value={searchText}
              onChange={(e) => {
                setSearchText(e.target.value);
                setPage(0);
              }}
              placeholder={t('sharedSearch')}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="primary" />
                  </InputAdornment>
                ),
              }}
              sx={{ flex: 1 }}
            />
            <Stack direction="row" spacing={1}>
              <Button
                variant="contained"
                color="info"
                size="small"
                startIcon={<AddCircleIcon />}
                onClick={() => navigate('/settings/device')}
                disabled={deviceReadonly}
              >
                {t('sharedAdd')}
              </Button>
              {manager && (
                <>
                  <Button variant="contained" color="csv" size="small" startIcon={<FileDownloadIcon />} onClick={handleExportCsv}>
                    CSV
                  </Button>
                  <Button variant="contained" color="excel" size="small" startIcon={<TableChartIcon />} onClick={handleExportXlsx}>
                    Excel
                  </Button>
                  <Button variant="contained" color="pdf" size="small" startIcon={<PictureAsPdfIcon />} onClick={handleExportPdf}>
                    PDF
                  </Button>
                </>
              )}
            </Stack>
          </Stack>

          {/* Tabela */}
          <Paper sx={{ flex: 1, overflow: 'hidden', borderRadius: 2 }}>
            <TableContainer sx={{ maxHeight: 600 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell />
                    <TableCell>ID</TableCell>
                    <TableCell>{t('sharedName')}</TableCell>
                    <TableCell>{t('deviceIdentifier')}</TableCell>
                    <TableCell>{t('sharedDevicePlate')}</TableCell>
                    <TableCell>{t('sharedPhone')}</TableCell>
                    <TableCell>{t('deviceModel')}</TableCell>
                    <TableCell>{t('deviceLastUpdate')}</TableCell>
                    <TableCell>{t('positionIgnition')}</TableCell>
                    <TableCell>{t('eventAlarm')}</TableCell>
                    <TableCell>{t('positionBlocked')}</TableCell>
                    <TableCell>{t('deviceStatus')}</TableCell>
                    {manager && (
                      <TableCell>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <PeopleIcon fontSize="small" />
                          {t('settingsUsers')}
                        </Stack>
                      </TableCell>
                    )}
                    <TableCell>{t('sharedActions')}</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={13} align="center" sx={{ py: 8 }}>
                        <CircularProgress />
                      </TableCell>
                    </TableRow>
                  ) : paginated.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={13} align="center" sx={{ py: 8 }}>
                        <Typography variant="body2" color="text.secondary">
                          {t('sharedNoData')}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginated.map((row) => {
                      const pos = positionsById[row.positionId];
                      const status = row.status?.toLowerCase() || 'unknown';
                      const statusColor =
                        status === 'online' ? 'success' : status === 'offline' ? 'error' : 'warning';
                      return (
                        <TableRow hover key={row.id}>
                          <TableCell>
                            <Avatar alt={row.name} src={getDeviceIcon(row)} sx={{ width: 32, height: 32 }} />
                          </TableCell>
                          <TableCell>{row.id}</TableCell>
                          <TableCell>{row.name}</TableCell>
                          <TableCell>{row.uniqueId}</TableCell>
                          <TableCell>{row.attributes?.form?.plate || '-'}</TableCell>
                          <TableCell>{row.phone || '-'}</TableCell>
                          <TableCell>{row.model || '-'}</TableCell>
                          <TableCell>{row.lastUpdate ? formatDate(row.lastUpdate) : '-'}</TableCell>
                          <TableCell>
                            {pos?.attributes?.ignition !== undefined ? (
                              <Chip
                                label={formatBoolean(pos.attributes.ignition, t)}
                                color={pos.attributes.ignition ? 'success' : 'error'}
                                size="small"
                              />
                            ) : (
                              <Chip label={t('sharedNoData')} size="small" />
                            )}
                          </TableCell>
                          <TableCell>
                            {pos?.attributes?.alarm && (
                              <Chip label={formatAlarm(pos.attributes.alarm, t)} color="success" size="small" />
                            )}
                          </TableCell>
                          <TableCell>
                            {pos?.attributes?.blocked !== undefined ? (
                              <Tooltip title={pos.attributes.blocked ? t('positionBlocked') : t('positionUnlocked')}>
                                <Chip
                                  icon={
                                    pos.attributes.blocked ? (
                                      <LockIcon fontSize="small" />
                                    ) : (
                                      <LockOpenIcon fontSize="small" />
                                    )
                                  }
                                  label={pos.attributes.blocked ? t('sharedYes') : t('sharedNo')}
                                  color={pos.attributes.blocked ? 'error' : 'success'}
                                  size="small"
                                />
                              </Tooltip>
                            ) : (
                              <Chip label={t('sharedNoData')} size="small" />
                            )}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={getStatusText(row.status || 'unknown', t)}
                              color={statusColor}
                              size="small"
                              sx={{ fontWeight: 500, fontSize: '0.75rem' }}
                            />
                          </TableCell>
                          {manager && (
                            <TableCell>
                              <DeviceUsersValue deviceId={row.id} />
                            </TableCell>
                          )}
                            {/* Ícone de conexões */}
                            <TableCell>
                              <Stack direction="row" spacing={1} alignItems="center">
                                {/* Ações padrão com ícones coloridos */}
                                <CollectionActions
                                  itemId={row.id}
                                  editPath="/settings/device"
                                  endpoint="devices"
                                  setTimestamp={setTimestamp}
                                  customActions={[
                                    {
                                      key: 'connections',
                                      title: t('sharedConnections'),
                                      icon: <LinkIcon sx={{ color: theme.palette.info.main }} fontSize="small" />,
                                      handler: (id) => navigate(`/settings/device/${id}/connections`),
                                    },
                                  ]}
                                />
                              </Stack>
                            </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={filteredItems.length}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[10, 25, 50, 100]}
            />
          </Paper>
        </Box>
      </Box>
    </PageLayout>
  );
};

export default DevicesPage;
